import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Skeleton,
} from "@ancatag/at-design";
import { useQuery } from "@tanstack/react-query";
import {
	Activity,
	AlertCircle,
	ArrowRight,
	Clock,
	Folder,
	Key,
	Shield,
	TrendingUp,
	Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "wouter";
import { useDashboardStats } from "../hooks/admin/useDashboard";
import { usersService } from "../services/admin";
import type { IdVerification, PlatformStats, User } from "../types/admin";

export function SuperAdminDashboard() {
	const { data: stats, isLoading: statsLoading } = useDashboardStats();
	const [pendingApprovalUsers, setPendingApprovalUsers] = useState<User[]>([]);
	const [pendingIdVerificationUsers, setPendingIdVerificationUsers] = useState<
		IdVerification[]
	>([]);

	// Fetch pending approval users
	const { data: pendingApprovalData, isLoading: pendingApprovalLoading } =
		useQuery({
			queryKey: ["admin-pending-approval"],
			queryFn: usersService.getPendingApprovalUsers,
			refetchInterval: 60000, // Refetch every minute
		});

	// Fetch pending ID verification users
	const { data: pendingIdData, isLoading: pendingIdLoading } = useQuery({
		queryKey: ["admin-pending-id-verification"],
		queryFn: usersService.getPendingIdVerificationUsers,
		refetchInterval: 60000, // Refetch every minute
	});

	// Update state when data changes (React Query v5 - onSuccess is deprecated)
	useEffect(() => {
		if (pendingApprovalData) {
			setPendingApprovalUsers(pendingApprovalData);
		}
	}, [pendingApprovalData]);

	useEffect(() => {
		if (pendingIdData) {
			setPendingIdVerificationUsers(pendingIdData);
		}
	}, [pendingIdData]);

	const isLoading = statsLoading || pendingApprovalLoading || pendingIdLoading;

	if (isLoading) {
		return <SuperAdminDashboardSkeleton />;
	}

	const platformStats = stats as PlatformStats | undefined;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						Super Admin Dashboard
					</h1>
					<p className="text-muted-foreground mt-2">
						Manage users, API keys, projects, and monitor platform activity
					</p>
				</div>
			</div>

			{/* Platform Stats */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<Users className="w-4 h-4" />
							Total Users
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{platformStats?.users.total || 0}
						</div>
						<p className="text-xs text-muted-foreground">
							{platformStats?.users.active || 0} active,{" "}
							{platformStats?.users.suspended || 0} suspended
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<Folder className="w-4 h-4" />
							Projects
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{platformStats?.projects.total || 0}
						</div>
						<p className="text-xs text-muted-foreground">Total projects</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<Key className="w-4 h-4" />
							API Keys
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{platformStats?.apiKeys.total || 0}
						</div>
						<p className="text-xs text-muted-foreground">
							{platformStats?.apiKeys.active || 0} active,{" "}
							{platformStats?.apiKeys.blocked || 0} blocked
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<TrendingUp className="w-4 h-4" />
							Cache Hit Rate
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{platformStats?.usage.cacheHitRate.toFixed(1) || "0.0"}%
						</div>
						<p className="text-xs text-muted-foreground">
							{platformStats?.usage.todayRequests || 0} requests today
						</p>
					</CardContent>
				</Card>
			</div>

			{/* Pending Actions */}
			{(pendingApprovalUsers.length > 0 ||
				pendingIdVerificationUsers.length > 0) && (
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
					{/* Pending Approval */}
					{pendingApprovalUsers.length > 0 && (
						<Card>
							<CardHeader className="flex flex-row items-center justify-between">
								<CardTitle className="flex items-center gap-2">
									<Clock className="w-5 h-5 text-yellow-600" />
									Pending Approval
									<Badge variant="secondary" className="ml-2">
										{pendingApprovalUsers.length}
									</Badge>
								</CardTitle>
								<Link href="/n-r-ops/users">
									<Button variant="ghost" size="sm" className="gap-1">
										View All <ArrowRight className="w-4 h-4" />
									</Button>
								</Link>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									{pendingApprovalUsers.slice(0, 5).map((user) => (
										<div
											key={user.id}
											className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
										>
											<div>
												<p className="text-sm font-medium">{user.email}</p>
												<p className="text-xs text-muted-foreground">
													{user.name || "No name"}
												</p>
											</div>
											<Badge variant="outline" className="text-yellow-600">
												Pending
											</Badge>
										</div>
									))}
								</div>
							</CardContent>
						</Card>
					)}

					{/* Pending ID Verification */}
					{pendingIdVerificationUsers.length > 0 && (
						<Card>
							<CardHeader className="flex flex-row items-center justify-between">
								<CardTitle className="flex items-center gap-2">
									<Shield className="w-5 h-5 text-orange-600" />
									Pending ID Verification
									<Badge variant="secondary" className="ml-2">
										{pendingIdVerificationUsers.length}
									</Badge>
								</CardTitle>
								<Link href="/n-r-ops/users">
									<Button variant="ghost" size="sm" className="gap-1">
										View All <ArrowRight className="w-4 h-4" />
									</Button>
								</Link>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									{pendingIdVerificationUsers.slice(0, 5).map((user) => (
										<div
											key={user.id}
											className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
										>
											<div>
												<p className="text-sm font-medium">{user.email}</p>
												<p className="text-xs text-muted-foreground">
													Submitted{" "}
													{user.idVerificationSubmittedAt
														? new Date(
																user.idVerificationSubmittedAt,
															).toLocaleDateString()
														: "N/A"}
												</p>
											</div>
											<Badge variant="outline" className="text-orange-600">
												Review Required
											</Badge>
										</div>
									))}
								</div>
							</CardContent>
						</Card>
					)}
				</div>
			)}

			{/* Quick Links */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
				<Link href="/n-r-ops/users">
					<Card className="hover:bg-muted/50 transition-colors cursor-pointer">
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Users className="w-5 h-5" />
								User Management
							</CardTitle>
						</CardHeader>
						<CardContent>
							<p className="text-sm text-muted-foreground">
								Manage users, approve accounts, verify IDs
							</p>
						</CardContent>
					</Card>
				</Link>

				<Link href="/n-r-ops/api-keys">
					<Card className="hover:bg-muted/50 transition-colors cursor-pointer">
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Key className="w-5 h-5" />
								API Key Management
							</CardTitle>
						</CardHeader>
						<CardContent>
							<p className="text-sm text-muted-foreground">
								View and manage all API keys across the platform
							</p>
						</CardContent>
					</Card>
				</Link>

				<Link href="/n-r-ops/projects">
					<Card className="hover:bg-muted/50 transition-colors cursor-pointer">
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Folder className="w-5 h-5" />
								Project Management
							</CardTitle>
						</CardHeader>
						<CardContent>
							<p className="text-sm text-muted-foreground">
								View all projects and their configurations
							</p>
						</CardContent>
					</Card>
				</Link>

				<Link href="/n-r-ops/sessions">
					<Card className="hover:bg-muted/50 transition-colors cursor-pointer">
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Shield className="w-5 h-5" />
								Session Management
							</CardTitle>
						</CardHeader>
						<CardContent>
							<p className="text-sm text-muted-foreground">
								Monitor and revoke user sessions
							</p>
						</CardContent>
					</Card>
				</Link>

				<Link href="/n-r-ops/usage">
					<Card className="hover:bg-muted/50 transition-colors cursor-pointer">
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<Activity className="w-5 h-5" />
								Usage Analytics
							</CardTitle>
						</CardHeader>
						<CardContent>
							<p className="text-sm text-muted-foreground">
								Platform-wide usage metrics and analytics
							</p>
						</CardContent>
					</Card>
				</Link>

				<Link href="/n-r-ops/audit">
					<Card className="hover:bg-muted/50 transition-colors cursor-pointer">
						<CardHeader>
							<CardTitle className="flex items-center gap-2">
								<AlertCircle className="w-5 h-5" />
								Audit Logs
							</CardTitle>
						</CardHeader>
						<CardContent>
							<p className="text-sm text-muted-foreground">
								View platform-wide audit logs and admin actions
							</p>
						</CardContent>
					</Card>
				</Link>
			</div>
		</div>
	);
}

function SuperAdminDashboardSkeleton() {
	return (
		<div className="space-y-6">
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<Skeleton className="h-8 w-64 mb-2" />
					<Skeleton className="h-4 w-96" />
				</div>
			</div>
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
				{[1, 2, 3, 4].map((i) => (
					<Card key={i}>
						<CardHeader className="pb-2">
							<Skeleton className="h-4 w-24" />
						</CardHeader>
						<CardContent>
							<Skeleton className="h-8 w-16 mb-2" />
							<Skeleton className="h-3 w-32" />
						</CardContent>
					</Card>
				))}
			</div>
		</div>
	);
}
